import { Fragment, useMemo, useState } from 'react';
import type {
  AlertVisibility,
  BulkAlertAction,
  BulkAlertSelection,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import type { BulkToolbarContext } from '@/components/DataTable/types';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';
import { useToast } from '@/hooks/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useBulkAlertAction,
  useBulkAlertEligibility,
  type BulkAlertScope,
} from '@/hooks/api/useBulkAlertActions';
import {
  bulkActionFailureToast,
  bulkActionSuccessToast,
  exclusionActions,
  modalCountsForAction,
  muteUnmuteActions,
  selectionSummary,
  selectAllAcrossPagesLabel,
  selectedAcrossPagesSummary,
  selectedAllMatchingSummary,
  type BulkBarAction,
} from './bulkActionCopy';
import { BulkActionConfirmDialog } from './BulkActionConfirmDialog';
import type { BulkActionDuration } from './BulkActionConfirmDialog';

export interface AlertsBulkActionBarProps {
  ctx: BulkToolbarContext<GroupedAlert>;
  /**
   * Scope the bulk action targets — an organization (manager/owner) or the
   * authenticated user's own alerts. Drives both the eligibility query and the
   * apply mutation, so the same bar serves the org and personal Alerts screens.
   */
  scope: BulkAlertScope;
  /** `alert_muting` on — enables the mute/unmute actions. */
  mutingEnabled: boolean;
  /** `alert_ignore` on — enables the exclude/remove-exclusion actions. */
  ignoreEnabled: boolean;
  /** Current view's suppression selector — scopes "select all across pages". */
  visibility: AlertVisibility;
  /**
   * Render the apply-action buttons disabled (support is read-only — KIR-4128).
   * Selection itself stays interactive: support may select rows and read the
   * eligibility summary, it just can't apply an action. "Disable, don't hide",
   * matching the per-row action buttons.
   */
  disabled?: boolean;
}

/**
 * Bulk-action bar for the Alerts screen (KIR-3252). Rendered in the DataTable's
 * toolbar slot when ≥1 row is selected. Shows the selection summary + the
 * cross-page "select all" affordance, derives state-aware action buttons from
 * the eligibility preview, and applies an all-or-nothing action with a single
 * success/failure toast (no undo).
 */
export function AlertsBulkActionBar({
  ctx,
  scope,
  mutingEnabled,
  ignoreEnabled,
  visibility,
  disabled = false,
}: AlertsBulkActionBarProps): React.ReactElement | null {
  const { toast } = useToast();
  const [activeModal, setActiveModal] = useState<{
    action: BulkAlertAction;
    eligibleCount: number;
    alreadyCount: number;
  } | null>(null);

  // Treat an active search term as a filter for copy purposes: the ticket's
  // copy rule says "matching filter/s" and doesn't carve out search, so a search
  // narrows the list the same way a column filter does. Count it as one filter.
  const hasSearch = ctx.search.trim().length > 0;
  const filterCount = ctx.filters.length + (hasSearch ? 1 : 0);
  const selectAll = ctx.selectAllAcrossPages;
  const excludedCount = ctx.excludedRowIds.length;
  // In select-all mode the effective selection is (total − excluded).
  const effectiveCount = selectAll
    ? Math.max(0, ctx.total - excludedCount)
    : ctx.selectedRowIds.length;

  const selection: BulkAlertSelection = useMemo(
    () =>
      selectAll
        ? {
            mode: 'query',
            filters: ctx.filters,
            search: ctx.search || undefined,
            // Scope "select all" to the current view so it matches the list.
            visibility,
            // "all matching EXCEPT these" — the user's deselections.
            ...(ctx.excludedRowIds.length > 0
              ? { excludeKeys: ctx.excludedRowIds }
              : {}),
          }
        : { mode: 'keys', alertKeys: ctx.selectedRowIds },
    [
      selectAll,
      ctx.filters,
      ctx.search,
      ctx.excludedRowIds,
      ctx.selectedRowIds,
      visibility,
    ]
  );

  // Coalesce rapid checkbox clicks into one eligibility request.
  const debouncedSelection = useDebouncedValue(selection, 300);
  const eligibilityEnabled =
    (mutingEnabled || ignoreEnabled) && effectiveCount > 0;
  const { data: eligibility, isFetching: eligibilityFetching } =
    useBulkAlertEligibility(scope, debouncedSelection, {
      enabled: eligibilityEnabled,
    });

  // The bar's labels/counts come from the eligibility of `debouncedSelection`,
  // but an action applies the LIVE `selection`. While the debounce is pending or
  // the preview is refetching they describe different sets (placeholderData even
  // keeps the prior counts) — so actions stay disabled until the preview matches
  // the live selection, guaranteeing the modal/confirm counts equal what's applied.
  const eligibilityPending =
    debouncedSelection !== selection || eligibilityFetching;

  const applyMutation = useBulkAlertAction(scope);

  const muteActions: BulkBarAction[] =
    mutingEnabled && eligibility ? muteUnmuteActions(eligibility) : [];
  const exclusion =
    ignoreEnabled && eligibility
      ? exclusionActions(eligibility)
      : { actions: [] as BulkBarAction[], ineligibleNote: undefined };
  const actionButtons = [...muteActions, ...exclusion.actions];

  const showSelectAllAcrossPages =
    !selectAll &&
    ctx.selectedRowIds.length > 0 &&
    ctx.isAllPageSelected &&
    ctx.pageCount > 1 &&
    ctx.total > ctx.selectedRowIds.length;

  // Keep the bar mounted while in select-all mode (even at 0 effective) so the
  // user always has a "Clear Selection" escape.
  if (!selectAll && ctx.selectedRowIds.length === 0) {
    return null;
  }

  let summary: string;
  if (!selectAll) {
    summary = selectionSummary({ count: effectiveCount, filterCount });
  } else if (excludedCount === 0) {
    summary = selectedAllMatchingSummary({
      total: ctx.total,
      pageCount: ctx.pageCount,
      filterCount,
    });
  } else {
    // Select-all WITH exclusions — keep the cross-page context.
    summary = selectedAcrossPagesSummary({
      count: effectiveCount,
      pageCount: ctx.pageCount,
      filterCount,
    });
  }

  const openActionModal = (action: BulkAlertAction): void => {
    // Don't open with counts that don't match the live selection yet.
    if (!eligibility || eligibilityPending) return;
    setActiveModal({ action, ...modalCountsForAction(action, eligibility) });
  };

  const confirmAction = (duration?: BulkActionDuration): void => {
    if (!activeModal) return;
    const { action } = activeModal;
    applyMutation.mutate(
      {
        action,
        selection,
        mutedUntil: duration?.mutedUntil,
        excludedUntil: duration?.excludedUntil,
      },
      {
        onSuccess: result => {
          toast({
            title: bulkActionSuccessToast(action, result.changedCount),
          });
          setActiveModal(null);
        },
        onError: () => {
          toast({
            title: bulkActionFailureToast(action),
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <div className="border-b border-[#dde6f7] bg-[#eef2fb] px-4 py-2 text-sm dark:border-[#1e2a44] dark:bg-[#11192e]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{summary}</span>

        {showSelectAllAcrossPages && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 font-medium underline underline-offset-2"
            onClick={ctx.enterSelectAllAcrossPages}
          >
            {selectAllAcrossPagesLabel({
              total: ctx.total,
              pageCount: ctx.pageCount,
              filterCount,
            })}
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="ml-1"
          onClick={ctx.clearSelection}
        >
          Clear Selection
        </Button>

        {actionButtons.map(btn => (
          <Fragment key={btn.action}>
            <span aria-hidden="true" className="text-muted-foreground">
              ·
            </span>
            {disabled ? (
              // Support read-only: a visually-disabled trigger inside a
              // focusable <span>, not a native `disabled` button. A disabled
              // button gets `pointer-events-none` and swallows hover/focus, so
              // the explanatory tooltip would never open. Mirrors the
              // AlertAddToListButton / AlertExcludeButton non-interactive branch.
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex">
                    <Button
                      variant="outline"
                      size="sm"
                      className="pointer-events-none opacity-50"
                      aria-disabled="true"
                      onClick={event => event.preventDefault()}
                    >
                      {btn.label}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={applyMutation.isPending || eligibilityPending}
                onClick={() => openActionModal(btn.action)}
              >
                {btn.label}
              </Button>
            )}
          </Fragment>
        ))}
      </div>

      {exclusion.ineligibleNote && (
        <p className="mt-1 text-muted-foreground">{exclusion.ineligibleNote}</p>
      )}

      {activeModal && (
        <BulkActionConfirmDialog
          open
          onOpenChange={open => {
            if (!open) setActiveModal(null);
          }}
          action={activeModal.action}
          eligibleCount={activeModal.eligibleCount}
          alreadyCount={activeModal.alreadyCount}
          isPending={applyMutation.isPending}
          onConfirm={confirmAction}
        />
      )}
    </div>
  );
}

AlertsBulkActionBar.displayName = 'AlertsBulkActionBar';
